import { ChangeDetectionStrategy, Component, Input, ViewEncapsulation } from '@angular/core';
import { IBalooSyncLog, IBalooSyncLogCategory, IBalooSyncLogItem } from '../../baloo';
import { AppTextService } from '@fi-shared/app-text';
import { BalooSyncLogCategoryType } from '@fi-app/external-platform-integration/baloo/baloo-sync-log/baloo-sync-log-category-type';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { getObjectTypeNameTextId } from '@fi-shared/utils';
import { flattenSimple } from '@fi-shared/utils/flatten-simple';
import { BalooHelperService } from '@fi-app/external-platform-integration/baloo/baloo-helper.service';

@Component({
    selector: 'fi-baloo-sync-log-details',
    templateUrl: './baloo-sync-log-details.component.html',
    styleUrls: ['./baloo-sync-log-details.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-baloo-sync-log-details' },
    standalone: false,
})
export class BalooSyncLogDetailsComponent {
    @Input() syncLog: IBalooSyncLog;
    @Input() syncLogHeading: string;
    @Input() isTestSync: boolean = true;
    @Input() syncItemActionName: string;
    @Input() syncInProgressLabel: string;
    @Input() isSyncLogDateSectionShown: boolean = true;

    filterItemsName: string[] = [];

    constructor(
        private appTextService: AppTextService,
        private customerDataService: FiCustomerDataService,
        private balooHelperService: BalooHelperService,
    ) {}

    onSelectedFilterItemsChange(filterNames: string[]) {
        this.filterItemsName = filterNames;
    }

    isEmptyListShown(): boolean {
        return !this.getFilteredLogCategories()?.length && !!this.filterItemsName.length;
    }

    getFilteredLogCategories(): IBalooSyncLogCategory[] {
        if (!this.filterItemsName?.length) return this.syncLog.LogCategories;

        return this.syncLog.LogCategories.filter((category: IBalooSyncLogCategory) => {
            return this.isCategoryFitsFilter(category);
        });
    }

    isCategoryFitsFilter(category: IBalooSyncLogCategory): boolean {
        return this.filterItemsName.some((filterItemName: string) => {
            return category.Items.find((categoryItem: IBalooSyncLogItem) => {
                return this.isCategoryItemFitsFilter(categoryItem, filterItemName);
            });
        });
    }

    isCategoryItemVisible(categoryItem: IBalooSyncLogItem): boolean {
        if (!this.filterItemsName?.length) return true;

        return this.filterItemsName.some((filterItemName: string) => {
            return this.isCategoryItemFitsFilter(categoryItem, filterItemName);
        });
    }

    private isCategoryItemFitsFilter(categoryItem: IBalooSyncLogItem, filterItemName: string): boolean {
        const filterItemNameLowerCase: string = filterItemName.toLowerCase();
        return (
            categoryItem.BalooNewName?.toLowerCase().includes(filterItemNameLowerCase) ||
            categoryItem.BalooOldName?.toLowerCase().includes(filterItemNameLowerCase) ||
            categoryItem.BalooOldEmail?.toLowerCase().includes(filterItemNameLowerCase) ||
            categoryItem.BalooNewEmail?.toLowerCase().includes(filterItemNameLowerCase)
        );
    }

    getSyncItems(): string[] {
        const allCategoryItems: IBalooSyncLogItem[][] = this.syncLog.LogCategories.map(
            (logCategory: IBalooSyncLogCategory) => logCategory.Items,
        );
        return flattenSimple<IBalooSyncLogItem[]>(allCategoryItems).map(
            (logItem: IBalooSyncLogItem) => logItem.BalooNewName,
        );
    }

    getSyncSummaryHeading(): string {
        const categoryItemsAmount: number = this.getSyncItems()?.length;
        return this.appTextService.get('baloo.sync_log_summary_heading', {
            '{0}': categoryItemsAmount,
            '{1}': this.appTextService
                .get(getObjectTypeNameTextId(this.syncLog.ObjectType, categoryItemsAmount !== 1))
                .toLowerCase(),
            '{2}': this.customerDataService.customerData.DisplayName,
        });
    }

    getSummaryLogText(): string {
        return this.balooHelperService.getSummaryLogText(this.syncLog);
    }
    getCategoryName(logCategoryType: BalooSyncLogCategoryType): string {
        return this.balooHelperService.getCategoryName(logCategoryType, this.syncLog.ObjectType);
    }
    getLogText(logItem: IBalooSyncLogItem): string {
        return this.balooHelperService.getLogText(logItem);
    }
    getSyncLogDownloadFileName(): string {
        return this.balooHelperService.getSyncLogDownloadFileName(this.syncLog.CreatedOn);
    }
}
